import { h } from 'preact'
import { useState, useEffect, useRef, useLayoutEffect } from 'preact/hooks'
import { safeT } from './ActivityIndicator.jsx'

/**
 * Merge tool_start + tool_end pairs: when a tool_end arrives, replace the
 * matching tool_start in-place so the timeline shows a single completed row
 * instead of two separate rows.
 */
function mergeToolEntries(entries) {
  const result = []
  const pendingStart = new Map() // toolName → index in result

  for (const entry of entries) {
    if (entry.type === 'start') {
      const key = entry.toolName || ''
      pendingStart.set(key, result.length)
      result.push(entry)
    } else if (entry.type === 'end') {
      const key = entry.toolName || ''
      const startIdx = pendingStart.get(key)
      if (startIdx !== undefined) {
        result[startIdx] = { ...entry, type: 'end' }
        pendingStart.delete(key)
      } else {
        result.push(entry)
      }
    } else {
      result.push(entry)
    }
  }

  return result
}

/**
 * SubagentPanel — collapsible activity panel for a SINGLE subagent run.
 *
 * Multiple parallel runs of the same subagent (different `instanceId`) get
 * their own SubagentPanel instance, rendered side-by-side by ChatThread.
 *
 * Props: { agent, i18n, formatter }
 * agent: {
 *   name: string,
 *   instanceId: string | null,
 *   tools: [{type:'start'|'end'|'text', toolName?, text?}],  // temporal timeline entries
 *   currentTool: string | null,   // tool currently in flight inside this subagent
 *   active: boolean,              // false only after `subagent_done` (or RUN_FINISHED)
 * }
 */
export function SubagentPanel({ agent, i18n, formatter }) {
  const [collapsed, setCollapsed] = useState(false)
  const [userToggled, setUserToggled] = useState(false)

  // Auto-collapse when the subagent finishes (active → false) unless user toggled manually.
  useEffect(() => {
    if (!agent.active && !userToggled) {
      setCollapsed(true)
    }
  }, [agent.active, userToggled])

  function toolLabel(toolName) {
    if (!toolName) return ''
    return safeT(i18n, `ai.status.tool_${toolName}`) || toolName
  }

  const mergedEntries = mergeToolEntries(agent.tools || [])

  return (
    <div class={`subagent-activity-panel${agent.active ? ' streaming' : ''}`}>
      <button
        type="button"
        class="subagent-panel-header"
        aria-expanded={String(!collapsed)}
        onClick={() => { setUserToggled(true); setCollapsed(c => !c) }}
      >
        <span class="subagent-panel-icon">
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
          </svg>
        </span>
        <span class="subagent-panel-name">{agent.name}</span>
        {agent.currentTool && agent.active && (
          <span class="subagent-panel-current-tool ms-2 small text-muted">
            › {toolLabel(agent.currentTool)}
          </span>
        )}
        {!agent.active && (
          <span class="subagent-panel-done-badge ms-2 small text-success">✓</span>
        )}
        {agent.active ? (
          <span class="subagent-panel-spinner">
            <svg width="12" height="12" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" class="opacity-75"/>
            </svg>
          </span>
        ) : null}
        <span class="subagent-panel-chevron ms-auto" style={collapsed ? 'transform:rotate(-90deg)' : ''}>▼</span>
      </button>

      <div class={`subagent-panel-body${collapsed ? ' collapsed' : ''}`}>
        {mergedEntries.map((entry, idx) => {
          if (entry.type === 'text') {
            return (
              <SubagentMarkdownBlock key={idx} text={entry.text} formatter={formatter} />
            )
          }
          if (entry.type === 'start') {
            return (
              <div key={idx} class="subagent-line subagent-line-start">
                <span class="subagent-line-arrow">▶</span>
                <span class="subagent-line-tool">{toolLabel(entry.toolName)}</span>
                <span class="subagent-line-status text-muted small ms-1">fut…</span>
              </div>
            )
          }
          if (entry.type === 'end') {
            return (
              <div key={idx} class="subagent-line subagent-line-end text-success opacity-75">
                <span class="subagent-line-check">✓</span>
                <span class="subagent-line-tool">{toolLabel(entry.toolName)}</span>
              </div>
            )
          }
          return null
        })}
      </div>
    </div>
  )
}

/**
 * SubagentMarkdownBlock — renders the subagent's streaming answer text as
 * markdown, same as the main AiBubble. Uses a ref + innerHTML to avoid
 * re-parsing the whole tree on every keystroke.
 */
function SubagentMarkdownBlock({ text, formatter }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    if (!ref.current) return
    if (formatter) {
      ref.current.innerHTML = formatter.parseMarkdown(text)
    } else {
      ref.current.textContent = text
    }
  }, [text, formatter])
  return <div class="subagent-text-block" ref={ref}></div>
}
